<p-card>
  <div class="flex items-center justify-between gap-2">
    <span class="flex items-baseline gap-0.5">
      <app-card-title key="common.filter" />
      <span class="text-button-red text-lg font-bold leading-none">.</span>
    </span>
    @if (hasActiveFilters()) {
      <p-button
        icon="pi pi-times"
        [label]="'hrTool.filter.reset' | transloco"
        severity="secondary"
        [text]="true"
        size="small"
        (onClick)="clearAllFilters()"
        data-testid="filter-reset-button"
      />
    }
  </div>

  @if (activeChips().length) {
    <div class="mb-3 mt-2 flex flex-wrap gap-1.5" data-testid="active-filter-chips">
      @for (chip of activeChips(); track chip.kind + '-' + chip.id) {
        <button
          type="button"
          class="hr-filter-chip"
          (click)="removeChip(chip)"
          [attr.data-testid]="'active-chip-' + chip.kind + '-' + chip.id"
        >
          <span class="max-w-40 truncate">{{ chip.label }}</span>
          <i class="pi pi-times"></i>
        </button>
      }
    </div>
  }

  <p-accordion
    class="accordion-dark"
    multiple="true"
    [value]="displayedOpenPanels()"
    (valueChange)="onUserToggled($event!)"
  >
    <p-accordion-panel value="functions" [attr.data-testid]="'accordion-panel-functions'">
      <p-accordion-header [attr.data-testid]="'filter-functions-header'"
        >{{ 'common.functions' | transloco }}
      </p-accordion-header>
      <p-accordion-content [attr.data-testid]="'filter-functions-content'">
        <p-accordion
          [multiple]="true"
          [value]="displayedModulePanels()"
          (valueChange)="onModuleToggled($event!)"
        >
          @for (module of moduleFunctionsMap().values(); track module.id) {
            <p-accordion-panel
              [value]="module.id"
              [attr.data-testid]="'filter-module-panel-' + module.id"
            >
              <p-accordion-header [attr.data-testid]="'filter-module-header-' + module.id">
                <ng-template #toggleicon let-active="active">
                  <i class="pi" [ngClass]="active ? 'pi-minus' : 'pi-plus'"></i>
                </ng-template>
                <span class="flex flex-1 items-center justify-between gap-2 pr-2">
                  <span>{{ module.translations | translateDirectus }}</span>
                  <span
                    role="button"
                    tabindex="0"
                    class="hr-module-toggle-all"
                    (click)="toggleAllInModule(module, $event)"
                    (keydown.enter)="toggleAllInModule(module, $event)"
                    [attr.data-testid]="'filter-module-toggleall-' + module.id"
                  >
                    {{
                      (isAllInModuleSelected(module) ? 'hrTool.filter.none' : 'hrTool.filter.all')
                        | transloco
                    }}
                  </span>
                </span>
              </p-accordion-header>
              <p-accordion-content class="ml-3">
                @for (moduleFunction of module.functions; track moduleFunction.id) {
                  <div
                    class="flex justify-between items-center pb-3 ml-3"
                    [attr.data-testid]="'filter-function-' + moduleFunction.id"
                  >
                    <app-module-function-label
                      [translations]="moduleFunction.translations!"
                      [wrap]="true"
                    />
                    <p-checkbox
                      [ngModel]="selectedFunctionIds.has(moduleFunction.id)"
                      (onChange)="onModuleFunctionFilterChange(moduleFunction.id, $event!.checked)"
                      [binary]="true"
                      [attr.data-testid]="'filter-function-checkbox-' + moduleFunction.id"
                      class="pr-2"
                    ></p-checkbox>
                  </div>
                }
              </p-accordion-content>
            </p-accordion-panel>
          }
        </p-accordion>
      </p-accordion-content>
    </p-accordion-panel>
    <p-accordion-panel value="serviceModels" [attr.data-testid]="'accordion-panel-serviceModels'">
      <p-accordion-header [attr.data-testid]="'filter-serviceModels-header'"
        >{{ 'hr-tool.service-model' | transloco }}
      </p-accordion-header>
      <p-accordion-content [attr.data-testid]="'filter-serviceModels-content'" class="ml-3">
        <form [formGroup]="serviceModelForm">
          <div formArrayName="selectedServiceModels">
            @for (m of availableServiceModels; track m.id; let i = $index) {
              <div
                class="flex justify-between items-center py-2"
                [attr.data-testid]="'filter-serviceModel-' + m.id"
              >
                <span>{{ (m.translationKey | transloco) || m.name }}</span>
                <p-checkbox
                  [attr.data-testid]="'filter-serviceModel-checkbox-' + m.id"
                  binary="true"
                  [formControlName]="i"
                  (onChange)="onServiceModelFilterChange(i)"
                  [inputId]="'sm-' + m.id"
                ></p-checkbox>
              </div>
            } @empty {
              <p data-testid="no-serviceModels">No service models available.</p>
            }
          </div>
        </form>
      </p-accordion-content>
    </p-accordion-panel>
    <p-accordion-panel value="companySize" [attr.data-testid]="'accordion-panel-companySize'">
      <p-accordion-header [attr.data-testid]="'filter-companySize-header'"
        >{{ 'hrTool.companySize' | transloco }}
      </p-accordion-header>
      <p-accordion-content [attr.data-testid]="'filter-companySize-content'" class="ml-3">
        <div class="flex flex-col gap-2">
          <p-inputNumber
            inputId="companySizeInput"
            [formControl]="companySizeControl"
            mode="decimal"
            [min]="0"
            [showButtons]="true"
            [placeholder]="'hrTool.enterCompanySize' | transloco"
            [attr.data-testid]="'filter-companySize-input'"
            class="w-full"
          ></p-inputNumber>
        </div>
      </p-accordion-content>
    </p-accordion-panel>

    <p-accordion-panel value="aiCapabilities" [attr.data-testid]="'accordion-panel-aiCapabilities'">
      <p-accordion-header [attr.data-testid]="'filter-aiCapabilities-header'">
        <span class="flex items-center gap-2">
          {{ 'hrTool.aiCapabilities' | transloco }}
          <app-directus-tooltip [tooltip]="'hrTool.aiCapabilities.tooltip' | transloco" />
        </span>
      </p-accordion-header>
      <p-accordion-content [attr.data-testid]="'filter-aiCapabilities-content'" class="ml-3">
        <app-hr-tool-ai-capability-filter
          [tools]="aiCapabilityFacetTools()"
          [selected]="selectedAiCapabilities()"
          (selectionChange)="onAiCapabilityFilterChange($event)"
        />
      </p-accordion-content>
    </p-accordion-panel>

    <p-accordion-panel value="certifications" [attr.data-testid]="'accordion-panel-certifications'">
      <p-accordion-header [attr.data-testid]="'filter-certifications-header'"
        >{{ 'hrTool.certifications' | transloco }}
      </p-accordion-header>
      <p-accordion-content [attr.data-testid]="'filter-certifications-content'" class="ml-3">
        <form [formGroup]="certificationForm">
          <div formArrayName="selectedCertifications">
            @for (cert of availableCertifications(); track cert.id; let i = $index) {
              <div
                class="flex justify-between items-center py-2"
                [attr.data-testid]="'filter-certification-' + cert.id"
              >
                <app-certification-label [certification]="cert" />
                <p-checkbox
                  [attr.data-testid]="'filter-certification-checkbox-' + cert.id"
                  binary="true"
                  [formControlName]="i"
                  (onChange)="onCertificationFilterChange(i)"
                  [inputId]="'cert-' + i"
                ></p-checkbox>
              </div>
            } @empty {
              <p data-testid="no-certifications">No certifications available.</p>
            }
          </div>
        </form>
      </p-accordion-content>
    </p-accordion-panel>
    <p-accordion-panel value="countries" [attr.data-testid]="'accordion-panel-countries'">
      <p-accordion-header [attr.data-testid]="'filter-countries-header'"
        >{{ 'hrTool.availableCountries' | transloco }}
      </p-accordion-header>
      <p-accordion-content [attr.data-testid]="'filter-countries-content'" class="ml-3">
        @if (sortedAvailableCountries()) {
          <app-country-multiselect
            [countries]="sortedAvailableCountries()"
            [preSelectedCountries]="selectedCountries"
            (selectedCountriesChange)="onCountriesSelected($event)"
          ></app-country-multiselect>
        }
        <app-country-list
          [countries]="selectedCountries"
          [heading]="'hrTool.selectedCountries' | transloco"
        />
      </p-accordion-content>
    </p-accordion-panel>

    <p-accordion-panel value="languages" [attr.data-testid]="'accordion-panel-languages'">
      <p-accordion-header [attr.data-testid]="'filter-languages-header'"
        >{{ 'hrTool.availableLanguages' | transloco }}
      </p-accordion-header>
      <p-accordion-content [attr.data-testid]="'filter-languages-content'" class="ml-3">
        @if (availableLanguages()) {
          <app-language-multiselect
            [languages]="sortedAvailableLanguages()"
            [preSelectedLanguages]="selectedLanguages"
            [activeLanguage]="activeLanguage()"
            (selectedLanguagesChange)="onLanguagesSelected($event)"
          ></app-language-multiselect>
        }
        @if (selectedLanguages.length) {
          <div class="mt-4">
            <h3 class="text-lg font-semibold mb-2">{{ 'hrTool.selectedLanguages' | transloco }}</h3>
            <ul class="list-none">
              @for (language of selectedLanguages; track language.isoCode) {
                <li class="flex items-center mb-1">
                  <span>{{ language.translations | translateDirectus }}</span>
                </li>
              }
            </ul>
          </div>
        }
      </p-accordion-content>
    </p-accordion-panel>
  </p-accordion>
</p-card>
